import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { AddChartToDashboardCommand } from 'libs/end-to-end/src/dashboard';
import { getNextChartPosition } from 'libs/end-to-end/src/dashboard';
import { CreateOrUpdateDashboardChartDto } from 'libs/end-to-end/src/dashboard';
import { DraftDashboard } from 'libs/end-to-end/src/dashboard';
import { DraftDashboardChart } from 'libs/end-to-end/src/dashboard';
import { DashboardRecordDto } from 'libs/end-to-end/src/dashboard';
import { MoveDashboardChartCommand } from 'libs/end-to-end/src/dashboard';
import { MoveDashboardChartResponseDto } from 'libs/end-to-end/src/dashboard';
import { UpdateDashboardDto } from 'libs/end-to-end/src/dashboard';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { v4 as uuid } from 'uuid';
import { convertDashboardRecordToDraft } from 'libs/end-to-end/src/dashboards.utils';
import { updateGlobalFiltersInDraft } from 'libs/end-to-end/src/dashboards.utils';
import { updateChartInDraft } from 'libs/end-to-end/src/dashboards.utils';
import { FilterDefinitionForExtrasArray } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';

export interface DashboardChartToBeCreated {
  chart: DraftDashboardChart;
  state: 'pending' | 'success' | 'failure';
}

export interface DashboardDraftState {
  dashboardId: string;
  draft: DraftDashboard;
  record: DashboardRecordDto | null;
  dirty: boolean;
  stale: boolean;
}

type DashboardDraftAction =
  | {
      type: 'INIT_FROM_SERVER';
      dashboard: DashboardRecordDto;
    }
  | {
      type: 'UPDATE_DASHBOARD_NAME';
      dashboardName: string;
    }
  | {
      type: 'UPDATE_DASHBOARD_DATABASE_REVISION_ID';
      databaseRevisionId: string | null;
    }
  | {
      type: 'UPDATE_DASHBOARD_GLOBAL_FILTERS';
      globalFilters: FilterDefinitionForExtrasArray | null;
    }
  | {
      type: 'ADD_CHART';
      chart: DraftDashboardChart;
    }
  | {
      type: 'UPDATE_CHART';
      chart: DraftDashboardChart;
    }
  | {
      type: 'DELETE_CHART';
      draftId: string;
    }
  | {
      type: 'REORDER_CHARTS';
      orderedDraftIds: string[];
    }
  | { type: 'MARK_STALE' }
  | { type: 'RESET' };

type PersistState =
  | { state: 'idle' }
  | { state: 'pending' }
  | { state: 'success' }
  | { state: 'failure'; error?: unknown };

// eslint-disable-next-line complexity
const dashboardDraftReducer = (
  state: DashboardDraftState | null,
  action: DashboardDraftAction,
): DashboardDraftState | null => {
  switch (action.type) {
    case 'INIT_FROM_SERVER': {
      const { dashboard } = action;

      return {
        dashboardId: dashboard.id,
        draft: convertDashboardRecordToDraft(dashboard),
        record: dashboard,
        dirty: false,
        stale: false,
      };
    }

    case 'UPDATE_DASHBOARD_NAME': {
      if (!state) {
        return null;
      }
      return {
        ...state,
        draft: {
          ...state.draft,
          name: action.dashboardName,
        },
        dirty: true,
      };
    }

    case 'UPDATE_DASHBOARD_DATABASE_REVISION_ID': {
      if (!state) {
        return null;
      }
      return {
        ...state,
        draft: {
          ...state.draft,
          databaseRevisionId: action.databaseRevisionId,
        },
        dirty: true,
      };
    }

    case 'UPDATE_DASHBOARD_GLOBAL_FILTERS': {
      if (!state) {
        return null;
      }
      return {
        ...state,
        draft: updateGlobalFiltersInDraft({
          draft: state.draft,
          globalFilters: action.globalFilters,
        }),
        dirty: true,
      };
    }

    case 'ADD_CHART': {
      if (!state) {
        return null;
      }
      return {
        ...state,
        draft: {
          ...state.draft,
          charts: [...state.draft.charts, action.chart],
        },
        dirty: true,
      };
    }

    case 'UPDATE_CHART': {
      if (!state) {
        return null;
      }
      return {
        ...state,
        draft: updateChartInDraft({ draft: state.draft, chart: action.chart }),
        dirty: true,
      };
    }

    case 'DELETE_CHART': {
      if (!state) {
        return null;
      }
      return {
        ...state,
        draft: {
          ...state.draft,
          charts: state.draft.charts.filter(
            (c) => c.draftId !== action.draftId,
          ),
        },
        dirty: true,
      };
    }

    case 'REORDER_CHARTS': {
      if (!state) {
        return null;
      }

      const currentIds = state.draft.charts.map((c) => c.draftId);

      if (
        currentIds.length === action.orderedDraftIds.length &&
        currentIds.every((id, i) => id === action.orderedDraftIds[i])
      ) {
        return state;
      }

      const chartMap = new Map(state.draft.charts.map((c) => [c.draftId, c]));

      const nextCharts = action.orderedDraftIds
        .map((draftId) => chartMap.get(draftId) ?? null)
        .filter((c): c is DraftDashboardChart => c !== null);

      if (nextCharts.length !== action.orderedDraftIds.length) {
        return state;
      }

      return {
        ...state,
        draft: { ...state.draft, charts: nextCharts },
        dirty: true,
      };
    }

    case 'MARK_STALE':
      if (!state) {
        return null;
      }
      return { ...state, stale: true };

    case 'RESET':
      return null;
    default:
      throw guardExhaustive(action);
  }
};

export type FetchDashboardDetailsFn = (
  dashboardId: string,
) => Promise<DashboardRecordDto | null>;

export type UpdateDraftChartFn = (chart: DraftDashboardChart) => void;

export type ReorderDraftChartsFn = (order: string[]) => void;

export type InitDashboardDraftFn = (
  dashboard: DashboardRecordDto | null,
) => void;

export type AcknowledgeDashboardStaleFn = () => void;

export type UpdateDashboardDraftGlobalFiltersFn = (
  globalFilters: FilterDefinitionForExtrasArray | null,
) => void;

export interface DashboardsContext {
  newDashboardChartList: DashboardChartToBeCreated[];
  dashboardsList: DashboardRecordDto[] | null;
  removeNewDashboardChart: (args: { draftId: string }) => void;

  dashboardDraft: DashboardDraftState | null;

  initDashboardDraft: (dashboard: DashboardRecordDto | null) => void;

  addDraftChart: (
    input: CreateOrUpdateDashboardChartDto,
  ) => DraftDashboardChart;

  updateDraftChart: UpdateDraftChartFn;
  updateDashboardDraftName: (name: string) => void;
  updateDashboardDraftDatabaseRevisionId: (
    databaseRevisionId: string | null,
  ) => void;
  updateDashboardDraftGlobalFilters: UpdateDashboardDraftGlobalFiltersFn;
  updateDashboard: (
    id: string,
    dashboard: UpdateDashboardDto,
  ) => Promise<DashboardRecordDto | null>;

  deleteDraftChart: (draftId: string) => void;
  reorderDraftCharts: ReorderDraftChartsFn;

  persistDashboardDraft: () => Promise<DashboardRecordDto | null>;

  moveDashboardChart: (
    command: MoveDashboardChartCommand,
  ) => Promise<MoveDashboardChartResponseDto | null>;

  addChartToDashboard: (
    command: AddChartToDashboardCommand,
  ) => Promise<DashboardRecordDto | null>;

  fetchDashboardsList: () => Promise<DashboardRecordDto[] | null>;
  fetchDashboardDetails: FetchDashboardDetailsFn;

  createEmptyDashboard: (args?: {
    dashboard: UpdateDashboardDto;
  }) => Promise<DashboardRecordDto | null>;

  deleteDashboard: (args: {
    dashboardId: string;
  }) => Promise<EmptyObject | null>;

  duplicateDashboard: (args: {
    dashboardId: string;
  }) => Promise<DashboardRecordDto | null>;

  persistDashboardDraftState: PersistState;
  isDashboardStale: boolean;
  acknowledgeDashboardStale: AcknowledgeDashboardStaleFn;
}

export const DashboardsContext = createContext<DashboardsContext>(
  {} as DashboardsContext,
);

export const useDashboards = () => useContext(DashboardsContext);

export const DashboardsRenderer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { myObservation } = useObservation();
  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();
  const language = useLanguage();
  const { userAuthenticatedApiCall } = useUser();

  const [dashboardsList, setDashboardsList] = useState<
    DashboardRecordDto[] | null
  >(null);

  const [newDashboardChartList, setNewDashboardChartList] = useState<
    DashboardChartToBeCreated[]
  >([]);

  const [dashboardDraft, dispatchDraft] = useReducer(
    dashboardDraftReducer,
    null,
  );

  const [persistDashboardDraftState, setPersistDashboardDraftState] =
    useState<PersistState>({
      state: 'idle',
    });

  const channelRef = useRef<BroadcastChannel | null>(null);

  const [isDashboardStale, setIsDashboardStale] = useState(false);

  const acknowledgeDashboardStale = useCallback(() => {
    setIsDashboardStale(false);
  }, []);

  useEffect(() => {
    if (!dashboardDraft?.dashboardId) return;

    const channel = new BroadcastChannel('dashboard-updates');
    channelRef.current = channel;

    channel.onmessage = (event) => {
      const { type, dashboardId, dashboardIds } = event.data ?? {};

      // Hard persist from another tab (same dashboard)
      if (
        type === 'DASHBOARD_PERSISTED' &&
        dashboardId === dashboardDraft.dashboardId
      ) {
        dispatchDraft({ type: 'MARK_STALE' });
        setIsDashboardStale(true);
        return;
      }

      // Soft invalidation (e.g. move chart between dashboards)
      if (
        type === 'DASHBOARD_INVALIDATED' &&
        Array.isArray(dashboardIds) &&
        dashboardIds.includes(dashboardDraft.dashboardId)
      ) {
        dispatchDraft({ type: 'MARK_STALE' });
        setIsDashboardStale(true);
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [dashboardDraft?.dashboardId]);

  const urlPrefix = useMemo(() => {
    if (
      selectedOrganizationId &&
      selectedDatabaseId &&
      selectedDatabaseRevisionId
    ) {
      return `${getApiBaseUrl()}/organizations/${selectedOrganizationId}/databases/${selectedDatabaseId}/revisions/${selectedDatabaseRevisionId}`;
    }
    return null;
  }, [selectedOrganizationId, selectedDatabaseId, selectedDatabaseRevisionId]);

  const removeNewDashboardChart = useCallback(
    ({ draftId }: { draftId: string }) => {
      setNewDashboardChartList((charts) =>
        charts.filter((c) => c.chart.draftId !== draftId),
      );
    },
    [],
  );

  const initDashboardDraft = useCallback(
    (dashboard: DashboardRecordDto | null) => {
      if (dashboard) {
        dispatchDraft({ type: 'INIT_FROM_SERVER', dashboard });
      } else {
        dispatchDraft({ type: 'RESET' });
      }
    },
    [],
  );

  const addDraftChart = useCallback(
    // eslint-disable-next-line complexity
    (input: CreateOrUpdateDashboardChartDto): DraftDashboardChart => {
      const chart: DraftDashboardChart = {
        ...input,
        draftId: uuid(),
        name:
          input.name ??
          getTranslatedContentAsString(
            language.savedAnalysisCardTitle({
              myObservation,
              request: input.customVizRequest,
            }),
          ),
        description: input.description,
        positionOnDashboard: input.positionOnDashboard,
        positionOnRow: input.positionOnRow,
        width: input.width,
        height: input.height,
        defaultViewMode: input.defaultViewMode,
        viewConfiguration: input.viewConfiguration,
      };

      dispatchDraft({ type: 'ADD_CHART', chart });
      return chart;
    },
    [language, myObservation],
  );

  const updateDraftChart = useCallback((chart: DraftDashboardChart) => {
    dispatchDraft({ type: 'UPDATE_CHART', chart });
  }, []);

  const deleteDraftChart = useCallback((draftId: string) => {
    dispatchDraft({ type: 'DELETE_CHART', draftId });
    setNewDashboardChartList((prev) =>
      prev.filter((x) => x.chart.draftId !== draftId),
    );
  }, []);

  const reorderDraftCharts = useCallback((order: string[]) => {
    dispatchDraft({ type: 'REORDER_CHARTS', orderedDraftIds: order });
  }, []);

  const updateDashboardDraftName = useCallback((name: string) => {
    dispatchDraft({
      type: 'UPDATE_DASHBOARD_NAME',
      dashboardName: name,
    });
  }, []);

  const updateDashboardDraftDatabaseRevisionId = useCallback(
    (databaseRevisionId: string | null) => {
      dispatchDraft({
        type: 'UPDATE_DASHBOARD_DATABASE_REVISION_ID',
        databaseRevisionId,
      });
    },
    [],
  );

  const updateDashboardDraftGlobalFilters = useCallback(
    (globalFilters: FilterDefinitionForExtrasArray | null) => {
      dispatchDraft({
        type: 'UPDATE_DASHBOARD_GLOBAL_FILTERS',
        globalFilters,
      });
    },
    [],
  );

  // todo: we should probably use useAsyncCallback but we need to think through carefully since this has implications
  // (i.e., navigating away would cause the this to stop.)
  const updateDashboard = useCallback(
    async (id: string, dashboard: UpdateDashboardDto) => {
      if (!urlPrefix) {
        return null;
      }

      const response = await userAuthenticatedApiCall({
        url: `${urlPrefix}/dashboards/${id}`,
        responseSchema: DashboardRecordDto,
        requestOptions: {
          method: 'PUT',
          body: JSON.stringify(dashboard),
        },
      });

      if (response?.resultType === 'ok') {
        dispatchDraft({ type: 'INIT_FROM_SERVER', dashboard: response.value });
        setNewDashboardChartList([]);

        setDashboardsList((prev) => {
          if (!prev) return [response.value];

          const exists = prev.some((d) => d.id === response.value.id);
          return exists
            ? prev.map((d) => (d.id === response.value.id ? response.value : d))
            : [...prev, response.value];
        });

        channelRef.current?.postMessage({
          type: 'DASHBOARD_PERSISTED',
          dashboardId: response.value.id,
        });

        return response.value;
      }

      return null;
    },
    [urlPrefix, userAuthenticatedApiCall],
  );

  const persistDashboardDraft = useCallback(async () => {
    if (!urlPrefix || !dashboardDraft?.dashboardId) {
      return null;
    }

    if (persistDashboardDraftState.state === 'pending') {
      return null;
    }

    if (dashboardDraft.stale) {
      setPersistDashboardDraftState({ state: 'idle' });
      alert(language.dashboardUpdatedElsewhere());
      return null;
    }

    setPersistDashboardDraftState({ state: 'pending' });

    const payload: UpdateDashboardDto = {
      name: dashboardDraft.draft.name,
      isPublic: dashboardDraft.draft.isPublic,
      globalFilters: dashboardDraft.draft.globalFilters,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      charts: dashboardDraft.draft.charts.map(({ draftId, ...chart }) => chart),
      databaseRevisionId: dashboardDraft.draft.databaseRevisionId,
    };

    const response = await userAuthenticatedApiCall({
      url: `${urlPrefix}/dashboards/${dashboardDraft.dashboardId}`,
      responseSchema: DashboardRecordDto,
      requestOptions: {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    });

    if (response?.resultType === 'ok') {
      dispatchDraft({ type: 'INIT_FROM_SERVER', dashboard: response.value });
      setNewDashboardChartList([]);

      setDashboardsList((prev) => {
        if (!prev) return [response.value];

        const exists = prev.some((d) => d.id === response.value.id);
        return exists
          ? prev.map((d) => (d.id === response.value.id ? response.value : d))
          : [...prev, response.value];
      });

      channelRef.current?.postMessage({
        type: 'DASHBOARD_PERSISTED',
        dashboardId: response.value.id,
      });

      setPersistDashboardDraftState({ state: 'success' });
      return response.value;
    }

    setPersistDashboardDraftState({ state: 'failure' });
    return null;
  }, [
    urlPrefix,
    dashboardDraft?.dashboardId,
    dashboardDraft?.stale,
    dashboardDraft?.draft.name,
    dashboardDraft?.draft.isPublic,
    dashboardDraft?.draft.globalFilters,
    dashboardDraft?.draft.charts,
    dashboardDraft?.draft.databaseRevisionId,
    persistDashboardDraftState.state,
    userAuthenticatedApiCall,
    language,
  ]);

  const moveDashboardChart = useCallback(
    async ({
      sourceDashboardId,
      sourceDashboardChartId,
      targetDashboardId,
    }: MoveDashboardChartCommand) => {
      if (!urlPrefix) return null;

      const response = await userAuthenticatedApiCall({
        url: `${urlPrefix}/dashboards/${sourceDashboardId}/charts/${sourceDashboardChartId}/move`,
        responseSchema: MoveDashboardChartResponseDto,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify({ targetDashboardId }),
        },
      });

      if (response?.resultType !== 'ok') {
        return null;
      }

      const { sourceDashboard, targetDashboard } = response.value;

      // If we are currently editing the source dashboard, hard reset draft
      if (dashboardDraft?.dashboardId === sourceDashboard.id) {
        dispatchDraft({
          type: 'INIT_FROM_SERVER',
          dashboard: sourceDashboard,
        });
      }

      // If we are editing the target dashboard, hard reset draft
      if (dashboardDraft?.dashboardId === targetDashboard.id) {
        dispatchDraft({
          type: 'INIT_FROM_SERVER',
          dashboard: targetDashboard,
        });
      }

      setDashboardsList((prev) => {
        if (!prev) return prev;

        return prev.map((d) => {
          if (d.id === sourceDashboard.id) return sourceDashboard;
          if (d.id === targetDashboard.id) return targetDashboard;
          return d;
        });
      });

      // Notify other tabs that both dashboards changed
      channelRef.current?.postMessage({
        type: 'DASHBOARD_INVALIDATED',
        dashboardIds: [sourceDashboard.id, targetDashboard.id],
      });

      return response.value;
    },
    [urlPrefix, userAuthenticatedApiCall, dashboardDraft?.dashboardId],
  );

  const fetchDashboardsList = useCallback(async () => {
    if (!urlPrefix) return null;

    const response = await userAuthenticatedApiCall({
      url: `${urlPrefix}/dashboards`,
      responseSchema: DashboardRecordDto.array(),
      requestOptions: { method: 'GET' },
    });

    if (response?.resultType === 'ok') {
      setDashboardsList(response.value);
      return response.value;
    }

    return null;
  }, [urlPrefix, userAuthenticatedApiCall]);

  const fetchDashboardDetails = useCallback(
    async (dashboardId: string) => {
      if (!urlPrefix) return null;

      const response = await userAuthenticatedApiCall({
        url: `${urlPrefix}/dashboards/${dashboardId}`,
        responseSchema: DashboardRecordDto,
        requestOptions: { method: 'GET' },
      });

      return response?.resultType === 'ok' ? response.value : null;
    },
    [urlPrefix, userAuthenticatedApiCall],
  );

  const addChartToDashboard = useCallback(
    async ({ chart, targetDashboardId }: AddChartToDashboardCommand) => {
      if (!urlPrefix) return null;

      const clientToastId = uuid();

      setNewDashboardChartList((charts) => [
        ...charts,
        {
          chart: {
            ...chart,
            id: clientToastId,
            draftId: clientToastId, // This is just needed to satisfy the type, since this list is used to power drafts.
            dashboardId: targetDashboardId,
          },
          state: 'pending',
        },
      ]);

      const fetchResponse = await fetchDashboardDetails(targetDashboardId);
      if (!fetchResponse) {
        setNewDashboardChartList((charts) =>
          charts.map((c) =>
            c.chart.id === clientToastId ? { ...c, state: 'failure' } : c,
          ),
        );
        return null;
      }

      const positionedChart = {
        ...chart,
        ...getNextChartPosition(fetchResponse.charts),
      };

      const payload: UpdateDashboardDto = {
        name: fetchResponse.name,
        isPublic: fetchResponse.isPublic,
        globalFilters: fetchResponse.globalFilters,
        charts: [...fetchResponse.charts, positionedChart],
        databaseRevisionId: fetchResponse.databaseRevisionId,
      };

      const response = await userAuthenticatedApiCall({
        url: `${urlPrefix}/dashboards/${targetDashboardId}`,
        responseSchema: DashboardRecordDto,
        requestOptions: {
          method: 'PUT',
          body: JSON.stringify(payload),
        },
      });

      if (response?.resultType === 'ok') {
        dispatchDraft({ type: 'INIT_FROM_SERVER', dashboard: response.value });

        channelRef.current?.postMessage({
          type: 'DASHBOARD_PERSISTED',
          dashboardId: response.value.id,
        });

        setNewDashboardChartList((charts) =>
          charts.map((c) =>
            c.chart.id === clientToastId ? { ...c, state: 'success' } : c,
          ),
        );

        return response.value;
      }

      setNewDashboardChartList((charts) =>
        charts.map((c) =>
          c.chart.id === clientToastId ? { ...c, state: 'failure' } : c,
        ),
      );
      return null;
    },
    [urlPrefix, fetchDashboardDetails, userAuthenticatedApiCall],
  );

  const createEmptyDashboard = useCallback(
    async (args?: { dashboard: UpdateDashboardDto }) => {
      if (!urlPrefix) return null;

      const dashboard: UpdateDashboardDto = args?.dashboard ?? {
        name: language.dashboardNewName({ now: new Date() }),
        charts: [],
        isPublic: false,
        globalFilters: null,
        databaseRevisionId: null,
      };

      const response = await userAuthenticatedApiCall({
        url: `${urlPrefix}/dashboards`,
        responseSchema: DashboardRecordDto,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(dashboard),
        },
      });

      if (response?.resultType === 'ok') {
        setDashboardsList((d) => [...(d ?? []), response.value]);
        return response.value;
      }

      return null;
    },
    [language, urlPrefix, userAuthenticatedApiCall],
  );

  const deleteDashboard = useCallback(
    async ({ dashboardId }: { dashboardId: string }) => {
      if (!urlPrefix) return null;

      const response = await userAuthenticatedApiCall({
        url: `${urlPrefix}/dashboards/${dashboardId}`,
        responseSchema: EmptyObject,
        requestOptions: { method: 'DELETE' },
      });

      if (response?.resultType === 'ok') {
        setDashboardsList((d) => d?.filter((x) => x.id !== dashboardId) ?? []);
        return response.value;
      }

      return null;
    },
    [urlPrefix, userAuthenticatedApiCall],
  );

  const duplicateDashboard = useCallback(
    async ({ dashboardId }: { dashboardId: string }) => {
      if (!urlPrefix) return null;

      const response = await userAuthenticatedApiCall({
        url: `${urlPrefix}/dashboards/${dashboardId}/duplicate`,
        responseSchema: DashboardRecordDto,
        requestOptions: { method: 'POST' },
      });

      if (response?.resultType === 'ok') {
        setDashboardsList((d) => [...(d ?? []), response.value]);
        return response.value;
      }

      return null;
    },
    [urlPrefix, userAuthenticatedApiCall],
  );

  const value = useMemo(
    () => ({
      dashboardsList,
      newDashboardChartList,
      removeNewDashboardChart,
      dashboardDraft,
      initDashboardDraft,
      addDraftChart,
      updateDraftChart,
      updateDashboardDraftName,
      updateDashboardDraftDatabaseRevisionId,
      updateDashboardDraftGlobalFilters,
      updateDashboard,
      deleteDraftChart,
      reorderDraftCharts,
      persistDashboardDraft,
      persistDashboardDraftState,
      moveDashboardChart,
      addChartToDashboard,
      fetchDashboardsList,
      fetchDashboardDetails,
      createEmptyDashboard,
      deleteDashboard,
      duplicateDashboard,
      isDashboardStale,
      acknowledgeDashboardStale,
    }),
    [
      dashboardsList,
      newDashboardChartList,
      removeNewDashboardChart,
      dashboardDraft,
      initDashboardDraft,
      addDraftChart,
      updateDraftChart,
      updateDashboardDraftName,
      updateDashboardDraftDatabaseRevisionId,
      updateDashboardDraftGlobalFilters,
      updateDashboard,
      deleteDraftChart,
      reorderDraftCharts,
      persistDashboardDraft,
      persistDashboardDraftState,
      moveDashboardChart,
      addChartToDashboard,
      fetchDashboardsList,
      fetchDashboardDetails,
      createEmptyDashboard,
      deleteDashboard,
      duplicateDashboard,
      isDashboardStale,
      acknowledgeDashboardStale,
    ],
  );

  useAsyncEffect(
    async (isAlive) => {
      if (!isAlive() || dashboardsList === null) {
        await fetchDashboardsList();
      }
    },
    [fetchDashboardsList, dashboardsList],
  );

  return (
    <DashboardsContext.Provider value={value}>
      {children}
    </DashboardsContext.Provider>
  );
};
